/**
 * Management Page
 * 
 * Displays management/jabatan page with CRUD operations.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Filter, Briefcase, Users, Building2, MapPin, AlertCircle, UserCheck } from "lucide-react";
import { JabatanTable } from "./components/JabatanTable";
import { JabatanDialog } from "./components/JabatanDialog";
import { DeleteJabatanDialog } from "./components/DeleteJabatanDialog";
import { AssignJabatanDialog } from "./components/AssignJabatanDialog";
import { AssignmentTable } from "./components/AssignmentTable";
import { useJabatan } from "./hooks/useJabatan";
import { useAssignments, useUpdateAssignment, useDeleteAssignment } from "./hooks/useAssignments";
import { toast } from "sonner";
import type { Jabatan, JabatanLevel, UserJabatanAssignment } from "@/services/managementService";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasEffectiveNasionalAdminAssignment } from "@/utils/privileges";

export function ManagementPage() {
  const { user } = useAuth();

  const hasNasionalLevel = React.useMemo(
    () => hasEffectiveNasionalAdminAssignment(user),
    [user]
  );
  
  const [activeTab, setActiveTab] = React.useState<"jabatan" | "assignments">(
    hasNasionalLevel ? "jabatan" : "assignments"
  );
  
  // Jabatan states
  const [selectedLevel, setSelectedLevel] = React.useState<JabatanLevel | undefined>(undefined);
  const { jabatan, loading, error, fetchJabatan } = useJabatan(selectedLevel);

  // Jabatan dialog states
  const [jabatanDialogOpen, setJabatanDialogOpen] = React.useState(false);
  const [selectedJabatan, setSelectedJabatan] = React.useState<Jabatan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [jabatanToDelete, setJabatanToDelete] = React.useState<Jabatan | null>(null);

  // Assignment states
  const { assignments, loading: assignmentsLoading, error: assignmentsError, fetchAssignments } = useAssignments();
  const { updateAssignment } = useUpdateAssignment();
  const { deleteAssignment } = useDeleteAssignment();

  // Assignment dialog states
  const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
  const [editAssignmentDialogOpen, setEditAssignmentDialogOpen] = React.useState(false);
  const [selectedAssignment, setSelectedAssignment] = React.useState<UserJabatanAssignment | null>(null);
  const [deleteAssignmentDialogOpen, setDeleteAssignmentDialogOpen] = React.useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = React.useState<UserJabatanAssignment | null>(null);

  // Calculate statistics
  const stats = React.useMemo(() => {
    const total = jabatan.length;
    const nasional = jabatan.filter((j) => j.level === "nasional").length;
    const provinsi = jabatan.filter((j) => j.level === "province").length;
    const kota = jabatan.filter((j) => j.level === "city").length;
    return { total, nasional, provinsi, kota };
  }, [jabatan]);

  // Handle create
  const handleCreate = () => {
    setSelectedJabatan(null);
    setJabatanDialogOpen(true);
  };

  // Handle edit
  const handleEdit = (jabatan: Jabatan) => {
    setSelectedJabatan(jabatan);
    setJabatanDialogOpen(true);
  };

  // Handle delete
  const handleDelete = (jabatan: Jabatan) => {
    setJabatanToDelete(jabatan);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!jabatanToDelete) return;

    try {
      const { managementService } = await import("@/services/managementService");
      await managementService.deleteJabatan(jabatanToDelete._id);
      toast.success("Jabatan berhasil dihapus", {
        description: `Jabatan "${jabatanToDelete.name}" telah dihapus dari sistem`,
      });
      fetchJabatan();
      setJabatanToDelete(null);
    } catch (error) {
      console.error("Error deleting jabatan:", error);
      toast.error("Gagal menghapus jabatan", {
        description: error instanceof Error ? error.message : "Terjadi kesalahan saat menghapus jabatan",
      });
    }
  };

  // Handle dialog success
  const handleDialogSuccess = () => {
    fetchJabatan();
    if (selectedJabatan) {
      toast.success("Jabatan berhasil diupdate", {
        description: `Jabatan "${selectedJabatan.name}" telah diperbarui`,
      });
    } else {
      toast.success("Jabatan berhasil ditambahkan", {
        description: "Jabatan baru telah ditambahkan ke sistem",
      });
    }
    setSelectedJabatan(null);
  };

  // Assignment handlers
  const handleAssign = () => {
    setAssignDialogOpen(true);
  };

  const handleAssignSuccess = () => {
    fetchAssignments();
    toast.success("Penugasan berhasil dibuat", {
      description: "Jabatan berhasil ditugaskan ke user",
    });
  };

  const handleEditAssignment = (assignment: UserJabatanAssignment) => {
    setSelectedAssignment(assignment);
    setEditAssignmentDialogOpen(true);
  };

  const handleUpdateAssignment = async (data: {
    province?: string;
    city?: string;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    notes?: string;
  }) => {
    if (!selectedAssignment) return;

    try {
      await updateAssignment(selectedAssignment._id, data);
      toast.success("Penugasan berhasil diupdate", {
        description: "Data penugasan telah diperbarui",
      });
      setEditAssignmentDialogOpen(false);
      setSelectedAssignment(null);
      fetchAssignments();
    } catch (error) {
      toast.error("Gagal mengupdate penugasan", {
        description: error instanceof Error ? error.message : "Terjadi kesalahan",
      });
    }
  };

  const handleDeleteAssignment = (assignment: UserJabatanAssignment) => {
    setAssignmentToDelete(assignment);
    setDeleteAssignmentDialogOpen(true);
  };

  const handleDeleteAssignmentConfirm = async () => {
    if (!assignmentToDelete) return;

    try {
      await deleteAssignment(assignmentToDelete._id);
      toast.success("Penugasan berhasil dihapus", {
        description: "Penugasan telah dihapus dari sistem",
      });
      setDeleteAssignmentDialogOpen(false);
      setAssignmentToDelete(null);
      fetchAssignments();
    } catch (error) {
      toast.error("Gagal menghapus penugasan", {
        description: error instanceof Error ? error.message : "Terjadi kesalahan",
      });
    }
  };

  return (
    <div className="flex-1 space-y-6 p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Briefcase className="h-8 w-8 text-primary" />
            Kepengurusan
          </h2>
          <p className="text-muted-foreground mt-1">
            Kelola jabatan kepengurusan di berbagai level organisasi
          </p>
        </div>
        {hasNasionalLevel && (
          <Button onClick={handleCreate} size="lg" className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Tambah Jabatan
          </Button>
        )}
      </div>

      {/* Statistics Cards - Only show for nasional level */}
      {hasNasionalLevel && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Jabatan</CardTitle>
            <Briefcase className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">Semua level</p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nasional</CardTitle>
            <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.nasional}</div>
            <p className="text-xs text-muted-foreground mt-1">Level nasional</p>
          </CardContent>
        </Card>

        <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Provinsi</CardTitle>
            <Building2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.provinsi}</div>
            <p className="text-xs text-muted-foreground mt-1">Level provinsi</p>
          </CardContent>
        </Card>

        <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kota/Kabupaten</CardTitle>
            <MapPin className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.kota}</div>
            <p className="text-xs text-muted-foreground mt-1">Level kota/kabupaten</p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "jabatan" | "assignments")} className="space-y-6">
        <TabsList>
          {hasNasionalLevel && (
            <TabsTrigger value="jabatan">
              <Briefcase className="h-4 w-4 mr-2" />
              Jabatan
            </TabsTrigger>
          )}
          <TabsTrigger value="assignments">
            <UserCheck className="h-4 w-4 mr-2" />
            Penugasan
          </TabsTrigger>
        </TabsList>

        {/* Jabatan Tab - Only show for nasional level */}
        {hasNasionalLevel && (
          <TabsContent value="jabatan" className="space-y-6">
          {/* Level Filter */}
          <Card className="border-2">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-lg">Filter</CardTitle>
              </div>
              <CardDescription>Filter berdasarkan level jabatan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Label htmlFor="level-filter" className="font-medium">Level</Label>
                <Select
                  value={selectedLevel || "all"}
                  onValueChange={(value) => {
                    setSelectedLevel(value === "all" ? undefined : (value as JabatanLevel));
                  }}
                >
                  <SelectTrigger id="level-filter" className="w-[220px]">
                    <SelectValue placeholder="Pilih level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Level</SelectItem>
                    <SelectItem value="nasional">Nasional</SelectItem>
                    <SelectItem value="province">Provinsi</SelectItem>
                    <SelectItem value="city">Kota/Kabupaten</SelectItem>
                  </SelectContent>
                </Select>
                {selectedLevel && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedLevel(undefined)}
                    className="text-xs"
                  >
                    Reset Filter
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Error Message */}
          {error && (
            <Card className="border-red-500 bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Jabatan Table */}
          <JabatanTable
            jabatan={jabatan}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>
        )}

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <UserCheck className="h-6 w-6 text-primary" />
                Penugasan Jabatan
              </h3>
              <p className="text-muted-foreground mt-1">
                Kelola penugasan jabatan kepada user
              </p>
            </div>
            <Button onClick={handleAssign} size="lg" className="shrink-0">
              <Plus className="h-4 w-4 mr-2" />
              Assign Jabatan
            </Button>
          </div>

          {/* Error Message */}
          {assignmentsError && (
            <Card className="border-red-500 bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <p className="text-red-600 dark:text-red-400 font-medium">{assignmentsError}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assignment Table */}
          <AssignmentTable
            assignments={assignments}
            loading={assignmentsLoading}
            onEdit={handleEditAssignment}
            onDelete={handleDeleteAssignment}
          />
        </TabsContent>
      </Tabs>

      {/* Jabatan Dialogs */}
      <JabatanDialog
        open={jabatanDialogOpen}
        onOpenChange={setJabatanDialogOpen}
        jabatan={selectedJabatan}
        onSuccess={handleDialogSuccess}
      />

      <DeleteJabatanDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        jabatan={jabatanToDelete}
        onConfirm={handleDeleteConfirm}
      />

      {/* Assignment Dialogs */}
      <AssignJabatanDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        onSuccess={handleAssignSuccess}
      />

      <EditAssignmentDialog
        open={editAssignmentDialogOpen}
        onOpenChange={setEditAssignmentDialogOpen}
        assignment={selectedAssignment}
        onSave={handleUpdateAssignment}
      />

      <DeleteAssignmentDialog
        open={deleteAssignmentDialogOpen}
        onOpenChange={setDeleteAssignmentDialogOpen}
        assignment={assignmentToDelete}
        onConfirm={handleDeleteAssignmentConfirm}
      />
    </div>
  );
}

// Edit Assignment Dialog Component
interface EditAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: UserJabatanAssignment | null;
  onSave: (data: {
    province?: string;
    city?: string;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    notes?: string;
  }) => Promise<void>;
}

function EditAssignmentDialog({ open, onOpenChange, assignment, onSave }: EditAssignmentDialogProps) {
  const [province, setProvince] = React.useState("");
  const [city, setCity] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);
  const [notes, setNotes] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (assignment && open) {
      setProvince(assignment.province || "");
      setCity(assignment.city || "");
      setStartDate(assignment.startDate ? new Date(assignment.startDate).toISOString().split("T")[0] || "" : "");
      setEndDate(assignment.endDate ? new Date(assignment.endDate).toISOString().split("T")[0] || "" : "");
      setIsActive(assignment.isActive !== false);
      setNotes(assignment.notes || "");
      setError(null);
    }
  }, [assignment, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave({
        province: province || undefined,
        city: city || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        isActive,
        notes: notes || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal mengupdate penugasan");
    } finally {
      setLoading(false);
    }
  };

  if (!assignment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <UserCheck className="h-6 w-6" />
            Edit Penugasan
          </DialogTitle>
          <DialogDescription>
            Update data penugasan untuk {assignment.user?.nama_lengkap || "User tidak ditemukan"} ({assignment.user?.callsign || "-"})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="province">Provinsi</Label>
                <Input
                  id="province"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  placeholder="Provinsi"
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Kota/Kabupaten</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Kota/Kabupaten"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Tanggal Mulai</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Tanggal Selesai</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="isActive">Status</Label>
              <Select
                value={isActive ? "active" : "inactive"}
                onValueChange={(value) => setIsActive(value === "active")}
                disabled={loading}
              >
                <SelectTrigger id="isActive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="inactive">Tidak Aktif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Catatan</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Catatan tambahan..."
                rows={3}
                disabled={loading}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Batal
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                "Simpan"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Delete Assignment Dialog Component
interface DeleteAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: UserJabatanAssignment | null;
  onConfirm: () => Promise<void>;
}

function DeleteAssignmentDialog({ open, onOpenChange, assignment, onConfirm }: DeleteAssignmentDialogProps) {
  const [deleting, setDeleting] = React.useState(false);

  const handleConfirm = async () => {
    if (!assignment) return;
    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting assignment:", error);
    } finally {
      setDeleting(false);
    }
  };

  if (!assignment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Hapus Penugasan?</DialogTitle>
          <DialogDescription>
            Apakah Anda yakin ingin menghapus penugasan untuk <strong>{assignment.user?.nama_lengkap || "User tidak ditemukan"}</strong> sebagai <strong>{assignment.jabatan.name}</strong>?
            Tindakan ini tidak dapat dibatalkan.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
          >
            Batal
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Menghapus...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

